import React, { useState, useEffect } from "react";
import { Dialog, Box, Typography, Checkbox, Button, TextField, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Menu, MenuItem } from "@mui/material";
import { formatPersonName } from "../../../utils/formatPersonName";

const TagManagerModal = ({ open, onClose, user, onUpdateTags }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [newTag, setNewTag] = useState({ name: "", color: "#E0E0E0" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialSelectedTags, setInitialSelectedTags] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editedName, setEditedName] = useState("");

  const colorPalette = [
    { name: "Cinza Claro", value: "#E0E0E0" },
    { name: "Verde", value: "#25D366" },
    { name: "Amarelo", value: "#FFCC00" },
    { name: "Vermelho", value: "#FF3B30" },
    { name: "Azul", value: "#007AFF" },
    { name: "Roxo", value: "#800080" },
    { name: "Laranja", value: "#FFA500" },
    { name: "Rosa", value: "#FF69B4" },
  ];

  useEffect(() => {
    if (open) {
      fetchTags();
    }
    if (user?.tags) {
      const initialTags = user.tags.map((tag) => tag.id);
      setSelectedTags(initialTags);
      setInitialSelectedTags(initialTags);
    } else {
      setSelectedTags([]);
      setInitialSelectedTags([]);
    }
  }, [open, user]);

  const fetchTags = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/users/tags`);
      if (!response.ok) throw new Error(`Erro: ${response.statusText}`);
      const data = await response.json();
      setAvailableTags(data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error("Erro ao carregar tags:", error);
      setError("Não foi possível carregar as etiquetas.");
      setAvailableTags([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (JSON.stringify(selectedTags) === JSON.stringify(initialSelectedTags)) {
      onClose();
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/users/${user.id}/tags`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tagIds: selectedTags }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro: ${response.status} - ${errorText}`);
      }
      const updatedUser = {
        ...user,
        tags: selectedTags.length > 0 ? availableTags.filter((tag) => selectedTags.includes(tag.id)) : [],
      };
      onUpdateTags(user.id, updatedUser);
      onClose();
    } catch (error) {
      console.error("Erro ao salvar etiquetas:", error);
      setError(`Erro ao salvar etiquetas: ${error.message}`);
    }
  };

  const handleTagChange = (tagId) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleCreateTag = async () => {
    if (!newTag.name) {
      setError("Nome é obrigatório.");
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newTag.name, color: newTag.color }),
      });
      if (!response.ok) throw new Error(`Erro: ${response.statusText}`);
      await fetchTags();
      setNewTag({ name: "", color: "#E0E0E0" });
      setError(null);
    } catch (error) {
      console.error("Erro ao criar etiqueta:", error);
      setError("Erro ao criar etiqueta.");
    }
  };

  const handleEditTag = async (tagId, newName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tags/${tagId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName }),
      });
      if (!response.ok) throw new Error(`Erro: ${response.statusText}`);
      await fetchTags();
      setEditingTagId(null);
      setError(null);
    } catch (error) {
      console.error("Erro ao editar etiqueta:", error);
      setError(`Erro ao editar etiqueta: ${error.message}`);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newTags = [...availableTags];
    [newTags[index - 1], newTags[index]] = [newTags[index], newTags[index - 1]];
    const updatedTags = newTags.map((tag, i) => ({ id: tag.id, order: i }));
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tags/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tags: updatedTags }),
      });
      if (!response.ok) throw new Error(`Erro: ${response.statusText}`);
      setAvailableTags(newTags);
    } catch (error) {
      console.error("Erro ao reordenar etiquetas:", error);
      setError("Erro ao reordenar etiquetas.");
      fetchTags();
    }
  };

  const handleMoveDown = async (index) => {
    if (index === availableTags.length - 1) return;
    const newTags = [...availableTags];
    [newTags[index], newTags[index + 1]] = [newTags[index + 1], newTags[index]];
    const updatedTags = newTags.map((tag, i) => ({ id: tag.id, order: i }));
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tags/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tags: updatedTags }),
      });
      if (!response.ok) throw new Error(`Erro: ${response.statusText}`);
      setAvailableTags(newTags);
    } catch (error) {
      console.error("Erro ao reordenar etiquetas:", error);
      setError("Erro ao reordenar etiquetas.");
      fetchTags();
    }
  };

  const handleDeleteTag = async (tagId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tags/${tagId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`Erro: ${response.statusText}`);

      await fetchTags();
      const updatedSelectedTags = selectedTags.filter((id) => id !== tagId);
      setSelectedTags(updatedSelectedTags);

      const updatedUser = {
        ...user,
        tags: updatedSelectedTags.length > 0 ? availableTags.filter((tag) => updatedSelectedTags.includes(tag.id)) : [],
      };
      onUpdateTags(user.id, updatedUser);

      setError(null);
    } catch (error) {
      console.error("Erro ao deletar etiqueta:", error);
      setError(`Erro ao deletar etiqueta: ${error.message}`);
    }
  };

  const handleOpenColorMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseColorMenu = () => {
    setAnchorEl(null);
  };

  const handleSelectColor = (color) => {
    setNewTag({ ...newTag, color });
    handleCloseColorMenu();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box sx={{ p: 2, minWidth: 300 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Gerenciar Etiquetas de {formatPersonName(user?.full_name) || "Usuário sem nome"}
        </Typography>
        <Typography variant="caption" sx={{ mb: 2, display: "block" }}>
          Use "Criar" para adicionar novas etiquetas e "Salvar" para associá-las ao usuário.
        </Typography>

        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            label="Nova Etiqueta"
            value={newTag.name}
            onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
            size="small"
            sx={{ flexGrow: 1 }}
          />
          <Button
            variant="outlined"
            onClick={handleOpenColorMenu}
            sx={{ minWidth: "auto", p: 0.5 }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: newTag.color,
                borderRadius: "50%",
                border: "2px solid #000",
              }}
            />
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseColorMenu}
            PaperProps={{
              sx: { boxShadow: 2, borderRadius: 1 },
            }}
          >
            {colorPalette.map((color) => (
              <MenuItem
                key={color.value}
                onClick={() => handleSelectColor(color.value)}
                sx={{ py: 0.5 }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    backgroundColor: color.value,
                    borderRadius: "50%",
                    mr: 1,
                  }}
                />
                {color.name}
              </MenuItem>
            ))}
          </Menu>
          <Button variant="contained" onClick={handleCreateTag} size="small">
            Criar
          </Button>
        </Box>

        {loading ? (
          <Typography>Carregando etiquetas...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            {availableTags.map((tag, index) => (
              <Box
                key={tag.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  backgroundColor: tag.color,
                  color: "#fff",
                  px: 1,
                  py: 0.5,
                  borderRadius: "12px",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  sx={{ color: "#fff" }}
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === availableTags.length - 1}
                  sx={{ color: "#fff" }}
                >
                  <ArrowDownwardIcon />
                </IconButton>
                <Checkbox
                  checked={selectedTags.includes(tag.id)}
                  onChange={() => handleTagChange(tag.id)}
                  color="primary"
                />
                {editingTagId === tag.id ? (
                  <TextField
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onBlur={() => handleEditTag(tag.id, editedName)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") handleEditTag(tag.id, editedName);
                    }}
                    autoFocus
                    size="small"
                    sx={{
                      input: {
                        color: "#fff",
                        bgcolor: tag.color,
                        border: "none",
                        "&:focus": { outline: "none" },
                      },
                    }}
                  />
                ) : (
                  <Typography sx={{ flexGrow: 1, mr: 1 }}>{tag.name}</Typography>
                )}
                <IconButton
                  size="small"
                  onClick={() => {
                    if (editingTagId === null) {
                      setEditingTagId(tag.id);
                      setEditedName(tag.name);
                    }
                  }}
                  sx={{ ...(editingTagId === tag.id && { display: "none" }) }}
                >
                  <EditIcon sx={{ color: "#fff" }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (window.confirm("Tem certeza que deseja deletar?")) {
                      handleDeleteTag(tag.id);
                    }
                  }}
                  sx={{ ...(editingTagId === tag.id && { display: "none" }) }}
                >
                  <DeleteIcon sx={{ color: "#fff" }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={onClose} color="secondary">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={JSON.stringify(selectedTags) === JSON.stringify(initialSelectedTags) || loading}
          >
            Salvar
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TagManagerModal;
